import type { IconName } from '../../components';
import type { FeatureFlagKey } from '../../config/featureFlags';
import { ROUTE } from '../../routes';
import type { WebsiteHubArea } from '../../theme/tokens';

export type WebsiteTabId = 'creatives' | 'logos' | 'art-style' | 'games' | 'blog' | 'seo';

/** The flag that puts the hub in place of the tab row: on, `/account/website` is the hub and each area is headed by
 *  the area header (a back button to the hub); off, the Website section as it was before the hub (the tab row, and
 *  `/account/website` landing on the first available tab). */
export const WEBSITE_HUB_FLAG: FeatureFlagKey = 'website-hub';

// Compile-time check: every tab has hub colours (`WEBSITE_HUB_AREA` is keyed by `WebsiteHubArea`), so a tab added
// here without its tokens fails the typecheck instead of rendering an uncoloured card.
const _areaCheck: WebsiteHubArea = null as unknown as WebsiteTabId;

export type WebsiteTab = {
  id: WebsiteTabId;
  /** A shared `Icon` name, or `logo-mark`: the "A" logo mark the Logos panel heads with (not in the shared set). */
  icon: IconName | 'logo-mark';
  path: string;
  /** The tab shows only while this flag is on (the route answers 404 otherwise). */
  flag?: FeatureFlagKey;
};

/** The Website section's tabs, in order. Each tab is its own route, so the URL always names the open tab. */
export const WEBSITE_TABS: readonly WebsiteTab[] = [
  { id: 'creatives', icon: 'image', path: ROUTE.creatives(), flag: 'creatives' },
  { id: 'logos', icon: 'logo-mark', path: ROUTE.logos() },
  { id: 'art-style', icon: 'palette', path: ROUTE.artStyle(), flag: 'art-style' },
  { id: 'games', icon: 'grid', path: ROUTE.gameLists(), flag: 'creatives-games' },
  { id: 'blog', icon: 'edit', path: ROUTE.blog() },
  { id: 'seo', icon: 'search', path: ROUTE.seo(), flag: 'website-seo' },
];

/** A tab's flag, the one the tab and its hub card show under (none: ungated): what the FTUX's pieces read too. */
export const websiteTabFlags = (id: WebsiteTabId): readonly FeatureFlagKey[] => {
  const flag = WEBSITE_TABS.find((tab) => tab.id === id)?.flag;
  return flag ? [flag] : [];
};

/**
 * The position of the tab whose own route `pathname` is (exactly, so the blog editor is not the Blog tab), else -1. A
 * trailing slash is ignored, as the router ignores it: `/account/website/logos/` is the Logos route.
 */
export const websiteTabIndex = (pathname: string): number => {
  const path = pathname.replace(/\/+$/, '');
  return WEBSITE_TABS.findIndex((tab) => tab.path === path);
};

/** Whether `pathname` is one of the Website tabs' own routes. */
export const isWebsiteTabPath = (pathname: string): boolean => websiteTabIndex(pathname) !== -1;
